import { Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IssuesDataService } from './issues.component.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import _ from 'lodash';
import { ITableColumn } from 'src/app/models/tableColumns';
import { issuesTableColumns } from 'src/app/models/tableColumns/issuesTableColumns.model';
import { IssuesTableRowViewModel } from 'src/app/models/viewModels/issuesTableRowViewModel';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { IssueModel } from 'src/app/models/api/issue.model';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { FeatureFlagsService } from '../../services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFalgsTypes.enum';

enum ResourceIcons {
  M365_COPILOT = 'assets/images/logo/m365Copilot.svg',
  SALESFORCE_EINSTEIN = 'assets/images/logo/salesForce-icon.svg',
  GPT_ENTERPRISE = 'assets/images/logo/openAI-icon.svg',
  COMPLAINT_REROUTING = 'assets/images/logo/copilotStudio.svg',
  TICKETING_AUTOMATION = 'assets/images/logo/copilotStudio.svg',
  BACKUP_AGENT = 'assets/images/logo/copilotStudio.svg',
  CREW_AI = 'assets/images/logo/crewai-icon.png',
  GOOGLE_GEMINI = 'assets/mockData/icons/gemini.svg',
  DEFAULT = 'assets/images/logo/agent-client-app-other.svg',
  CURSOR = 'assets/mockData/icons/cursor.svg',
}

@Component({
  selector: 'app-issues',
  templateUrl: './issues.component.html',
  styleUrls: ['./issues.component.scss'],
})
export class IssuesComponent implements OnInit {
  public columns = signal<ITableColumn[]>(issuesTableColumns);
  public issuesCurrentPage$ = new BehaviorSubject<IssuesTableRowViewModel[]>([]);
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public showNoData = false;
  public totalItems = 0;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public issuesData: IssueModel[] = [];
  public isFeatureEnabled = false;
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);

  public currentRulesViewModel: BehaviorSubject<BehaviorSubject<IssuesTableRowViewModel>[]>;
  private _sortOnChangeSubscription$: Subscription;

  constructor(
    private readonly _issuesDataService: IssuesDataService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _router: Router,
    private readonly _sidePanel: SidePanelService
  ) {
    this._initializePageInfo();
  }

  ngOnInit(): void {
    // Check if the feature is enabled
    this.isFeatureEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.IssuesPage);

    if (!this.isFeatureEnabled) {
      this._router.navigate(['/not-authorized']);
      return;
    }

    this.fetchIssuesData();
    this._subscribeToSortOnChange();
  }

  public fetchIssuesData(): void {
    this.isLoading$.next(true);
    this._issuesDataService.getIssuesData().subscribe(
      (data: IssueModel[]) => {
        this.issuesData = data;
        this.totalItems = this.issuesData.length;
        const viewModels = this.issuesData.map((item) => this._transformToViewModel(item));
        this.issuesCurrentPage$.next(viewModels);

        this.showNoData = viewModels.length === 1;
        this.isLoading$.next(false);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        console.error('Error fetching violations data:', error);
        this.showNoData = true;
        this.isLoading$.next(false);
        this.issuesCurrentPage$.next([]);
      }
    );
  }

  public rowSelected(selectedRow: IssuesTableRowViewModel): void {
    this._sidePanel.openIssueDetails(selectedRow);
  }

  public rowUnselected() {
    this._sidePanel.close();
  }

  public onColumnSortClick(sortData: SortColumn): void {
    this.sortedColumn$.next(sortData);
  }

  private _getIconForResource(primaryResource: string): string {
    return (
      ResourceIcons[primaryResource.replace(/\s+/g, '_').toUpperCase() as keyof typeof ResourceIcons] ||
      ResourceIcons.DEFAULT
    );
  }

  private _transformToViewModel(row: IssueModel): IssuesTableRowViewModel {
    return {
      rowId: row.id,
      id: row.id,
      name: row.name,
      status: row.status,
      severity: row.severity,
      cvssScore: row.cvssScore,
      businessCriticality: row.businessCriticality,
      primaryResource: row.primaryResource,
      primaryResourceIcon: this._getIconForResource(row.primaryResource),
      ownerCreatedBy: row.ownerCreatedBy,
      issue: row,
      clickable: true,
      menuActions: row.menuActions,
      ragFiles: row.ragFiles,
      risk: row.risk,
      primaryResourceType: row.primaryResourceType,
      labels: row.labels,
    };
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 1,
      maxSize: 2,
      totalItems: 1,
      totalPages: 1,
    });
  }

  private _subscribeToSortOnChange(): void {
    this._sortOnChangeSubscription$ = this.sortedColumn$.subscribe((newSort) => {
      if (newSort?.column.sortKey == 'issueName') {
        this._sortByNumbers(newSort.isDesc);
      } else if (newSort?.column.sortKey == 'severity') {
        this._sortBySeverity(newSort.isDesc);
      }
    });
  }

  private _sortByNumbers(isDesc: boolean): void {
    const isDescStr = isDesc ? 'desc' : 'asc';

    this.currentRulesViewModel = new BehaviorSubject(
      _.orderBy(this.currentRulesViewModel.value, ['value.issueName'], [isDescStr])
    );
  }

  private _sortBySeverity(isDesc: boolean): void {
    const isDescStr = isDesc ? 'desc' : 'asc';

    this.currentRulesViewModel = new BehaviorSubject(
      _.orderBy(this.currentRulesViewModel.value, ['value.severity'], [isDescStr])
    );
  }
}
